import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth, hasMinRole } from "@/contexts/AuthContext";
import { Layout } from "@/components/app/Layout";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import EmployeeDashboard from "@/pages/EmployeeDashboard";
import RoleGate from "@/components/app/RoleGate";
import Compliance from "@/pages/Compliance";
import Projects from "@/pages/Projects";
import Finance from "@/pages/Finance";
import HR from "@/pages/HR";
import Tasks from "@/pages/Tasks";
import Users from "@/pages/Users";
import AuditLog from "@/pages/AuditLog";
import Assets from "@/pages/Assets";
import MyAssetRequests from "@/pages/MyAssetRequests";
import MyProfile from "@/pages/MyProfile";
import Appointments from "@/pages/Appointments";
import Approvals from "@/pages/Approvals";
import Procurement from "@/pages/Procurement";
import Notifications from "@/pages/Notifications";
import Calendar from "@/pages/Calendar";
import MeetingMinutes from "@/pages/MeetingMinutes";
import Reports from "@/pages/Reports";
import EthicsReporting from "@/pages/EthicsReporting";
import VendorOnboardingPublic from "@/pages/VendorOnboardingPublic";
import TestDataControl from "@/pages/TestDataControl";
import Permissions from "@/pages/Permissions";
import PaymentRequests from "@/pages/PaymentRequests";
import Funders from "@/pages/Funders";
import BranchDashboard from "@/pages/BranchDashboard";
import Clients from "@/pages/Clients";

function AuthLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center" data-testid="auth-loading">
      <div className="font-mono-alt text-xs uppercase tracking-[0.3em] text-slate-400">
        Verifying session…
      </div>
    </div>
  );
}

function Forbidden() {
  return (
    <Layout>
      <div className="p-12" data-testid="forbidden-page">
        <div className="overline">403</div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Access denied</h1>
        <p className="mt-2 text-sm text-slate-500">
          You don’t have permission to view this section. Contact your administrator if you believe this is an error.
        </p>
      </div>
    </Layout>
  );
}

import {
  isFinance, isHR, isProcurement, isProjectManager, isCompliance,
  isAssetManager, can,
} from "@/lib/roles";

const SCOPE_CAPABILITY_GATE = {
  hr: isHR, finance: isFinance, pm: isProcurement, procurement: isProcurement,
  project: isProjectManager, compliance: isCompliance, assets: isAssetManager,
};

function Gate({ children, minRole, scopes, perm }) {
  const { user } = useAuth();
  if (user === null) return <AuthLoading />;
  if (user === false) return <Login />;

  // Permission Access Management — explicit grant opens the page, explicit
  // deny closes it, regardless of role rank.
  if (perm) {
    const p = can(user, perm);
    if (p === true) return <Layout>{children}</Layout>;
    if (p === false) return <Forbidden />;
  }

  // Allow specialist officers in the matching scope through even if their
  // numeric rank is below minRole. Backend still gates writes.
  let hasScopeCapability = false;
  if (scopes && scopes.length) {
    hasScopeCapability = scopes.some((s) => {
      const cap = SCOPE_CAPABILITY_GATE[s];
      return typeof cap === "function" && cap(user);
    });
  }

  if (minRole && !hasMinRole(user, minRole) && !hasScopeCapability) {
    return <Forbidden />;
  }
  return <Layout>{children}</Layout>;
}

function RootDashboard() {
  const { user } = useAuth();
  if (user?.role === "branch_user") return <BranchDashboard />;
  return <Dashboard />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter basename="/admin">
        <Routes>
          <Route path="/" element={<Gate><RootDashboard /></Gate>} />
          <Route path="/branch" element={<Gate><BranchDashboard /></Gate>} />
          <Route path="/me" element={<Gate><RoleGate allowEmployee><EmployeeDashboard /></RoleGate></Gate>} />
          <Route path="/compliance" element={<Gate><RoleGate scopes={["compliance"]}><Compliance /></RoleGate></Gate>} />
          <Route path="/projects" element={<Gate><RoleGate scopes={["pm"]}><Projects /></RoleGate></Gate>} />
          <Route path="/tasks" element={<Gate><RoleGate allowEmployee><Tasks /></RoleGate></Gate>} />
          <Route path="/payments" element={<Gate><RoleGate allowEmployee><PaymentRequests /></RoleGate></Gate>} />
          <Route path="/funders" element={<Gate><RoleGate scopes={["finance", "compliance", "pm"]}><Funders /></RoleGate></Gate>} />
          <Route path="/clients" element={<Gate perm="clients.view" minRole="manager" scopes={["finance", "compliance", "pm"]}><Clients /></Gate>} />
          <Route path="/assets" element={<Gate><RoleGate scopes={["pm"]}><Assets /></RoleGate></Gate>} />
          <Route path="/my-assets" element={<Gate><RoleGate allowEmployee><MyAssetRequests /></RoleGate></Gate>} />
          <Route path="/my-profile" element={<Gate><RoleGate allowEmployee><MyProfile /></RoleGate></Gate>} />
          <Route path="/appointments" element={<Gate><RoleGate><Appointments /></RoleGate></Gate>} />
          <Route path="/approvals" element={<Gate perm="approvals.view" minRole="manager" scopes={["hr", "finance", "compliance", "asset", "pm"]}><RoleGate perm="approvals.view" minRole="manager" scopes={["hr", "finance", "compliance", "asset", "pm"]} denyRoles={["compliance_officer"]}><Approvals /></RoleGate></Gate>} />
          <Route path="/finance" element={<Gate perm="finance.view" minRole="manager" scopes={["finance"]}><RoleGate perm="finance.view" minRole="manager" scopes={["finance"]}><Finance /></RoleGate></Gate>} />
          <Route path="/procurement" element={<Gate perm="procurement.view" minRole="manager" scopes={["pm", "finance", "procurement"]}><RoleGate perm="procurement.view" minRole="manager" scopes={["pm", "finance", "procurement"]}><Procurement /></RoleGate></Gate>} />
          <Route path="/hr" element={<Gate perm="hr.view" minRole="manager" scopes={["hr"]}><RoleGate perm="hr.view" minRole="manager" scopes={["hr"]}><HR /></RoleGate></Gate>} />
          <Route path="/users" element={<Gate perm="users.view" minRole="admin"><Users /></Gate>} />
          <Route path="/permissions" element={<Gate minRole="super_admin"><Permissions /></Gate>} />
          <Route path="/audit" element={<Gate perm="audit.view" minRole="admin"><AuditLog /></Gate>} />
          <Route path="/notifications" element={<Gate><RoleGate allowEmployee><Notifications /></RoleGate></Gate>} />
          <Route path="/calendar" element={<Gate><RoleGate allowEmployee><Calendar /></RoleGate></Gate>} />
          <Route path="/meetings" element={<Gate><RoleGate><MeetingMinutes /></RoleGate></Gate>} />
          <Route path="/reports" element={<Gate><RoleGate><Reports /></RoleGate></Gate>} />
          <Route path="/ethics" element={<Gate><RoleGate allowEmployee><EthicsReporting /></RoleGate></Gate>} />
          <Route path="/test-data" element={<Gate minRole="admin"><TestDataControl /></Gate>} />
          <Route path="/vendor-onboarding/:token" element={<VendorOnboardingPublic />} />
          <Route path="*" element={<Gate><RootDashboard /></Gate>} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
    </AuthProvider>
  );
}

export default App;
